import { IconPhoto, IconTrash } from '@tabler/icons-react';
import { type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { errorMessage } from '@/lib/api-client';
import type { Clinic } from '@/types/api';
import { resizeLogo } from '@/utils/image';
import { settingsApi } from '../api';
import { useLogoUrl } from '../useLogoUrl';

const FIELDS = ['name', 'address', 'pib', 'mb', 'phone', 'email'] as const;
type Field = (typeof FIELDS)[number];

function Logo({ clinic, onChange }: { clinic: Clinic; onChange: (c: Clinic) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const url = useLogoUrl(clinic.logoVersion);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const image = await resizeLogo(file);
      onChange(await settingsApi.setLogo(image.type, image.base64));
    } catch (err) {
      toast.show(
        err instanceof Error && err.message === 'logo too large'
          ? t('settings.clinic.logoTooLarge')
          : errorMessage(err),
        'error'
      );
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      onChange(await settingsApi.removeLogo());
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field">
      <span className="field__label">{t('settings.clinic.logo')}</span>
      <div className="logo-field">
        <div className="logo-field__preview">
          {url ? <img src={url} alt={t('settings.clinic.logo')} /> : <IconPhoto size={32} />}
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {t(clinic.logoVersion ? 'settings.clinic.logoReplace' : 'settings.clinic.logoUpload')}
          </button>
          {clinic.logoVersion && (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={remove}>
              <IconTrash size={16} /> {t('settings.clinic.logoRemove')}
            </button>
          )}
          <span className="small muted">{t('settings.clinic.logoHint')}</span>
        </div>
      </div>
    </div>
  );
}

/** Name, letterhead details (address, PIB, MB, phone, e-mail) and logo. */
export function ClinicDetails({
  clinic,
  onChange,
}: {
  clinic: Clinic;
  onChange: (c: Clinic) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const initial = () =>
    Object.fromEntries(FIELDS.map((f) => [f, clinic[f] ?? ''])) as Record<Field, string>;
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const changed = FIELDS.filter((f) => values[f].trim() !== (clinic[f] ?? ''));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = Object.fromEntries(changed.map((f) => [f, values[f].trim()]));
      const saved = await settingsApi.updateClinic(patch);
      onChange(saved);
      toast.show(t('settings.saved'));
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('settings.clinic.title')}</h2>
      </div>
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          {FIELDS.map((f) => (
            <div
              key={f}
              className={`field ${f === 'name' || f === 'address' ? 'form-grid__wide' : ''}`}
            >
              <label htmlFor={`clinic-${f}`}>{t(`settings.clinic.${f}`)}</label>
              <input
                id={`clinic-${f}`}
                className="input"
                type={f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'}
                inputMode={f === 'pib' || f === 'mb' ? 'numeric' : undefined}
                required={f === 'name'}
                value={values[f]}
                onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className="row">
          <button type="submit" className="btn btn--dark" disabled={busy || !changed.length}>
            {t('common.save')}
          </button>
        </div>
      </form>
      <hr className="divider" />
      <Logo clinic={clinic} onChange={onChange} />
    </section>
  );
}
